import { Injectable } from '@angular/core';
import { VehicleCostFormService } from '../forms/vehicle-cost-form.service';
import { iCalcTaxOverParams } from '@shared/models/technical/calcTaxOverParams';
import { GlobalConstant } from '@shared/GlobalConstant';
import { take } from 'rxjs/operators';
import { TaxReductParameters } from '@_dto/global/vehicleCostDto/taxReductParametersDto';
import { Comparison } from '@_models/comparison';
import { TcoBackendService } from '../api/tco-backend.service';
@Injectable({
	providedIn: 'root',
})
export class IncentivesService {
	constructor(protected readonly vehicleCostFormService: VehicleCostFormService, private readonly dataService: TcoBackendService) {}

	/**
	 * Compute Tax overamortization
	 */
	public calcTaxOver(calcTaxOverParams: iCalcTaxOverParams) {
		if (!calcTaxOverParams.taxReductState) {
			return;
		}

		calcTaxOverParams.comparisonsSelected.forEach((comparison) => this.updateTaxDetailsForEnergy(comparison.name, calcTaxOverParams));
	}

	public calcTaxOverBase(financials: any, energy: string): number {
		return (financials.electric.truck + financials.electric.body) - (financials[energy].truck + financials[energy].body);
	}

	public checkUpdatedTaxReductDateByCountry(taxReductUpdateDate: Date, limitDate: Date, countryCode: string): boolean {
		const isFrance = countryCode === GlobalConstant.FR;
		if(!taxReductUpdateDate === null && isFrance) {
			return false
		}
		const isOutdated = (new Date(taxReductUpdateDate) > new Date(limitDate) && isFrance);

		return !isOutdated;
	}

	public initTaxReductLimitDate(comparison:Comparison){
		this.dataService.getTaxReductParameters()
			.pipe(take(1))
			.subscribe((data: TaxReductParameters) => comparison.vehicleCost.energyIncentivesTaxe.taxReductLimitDate = new Date(data.parameterDateValue));
	}

	private updateTaxDetailsForEnergy(energy: string, calcTaxOverParams: iCalcTaxOverParams) {
		const energyIncentive = calcTaxOverParams.energyIncentivesTax[energy];
		const financials = calcTaxOverParams.financials[energy];
		const isOutdated = this.checkTaxReductOveramortization(calcTaxOverParams.taxReductUpdateDate, calcTaxOverParams.taxReductLimitDate);
		const totalOver = this.computeTotalOver(calcTaxOverParams, energy, isOutdated, energyIncentive.taxOver, financials);
		const totalProfit = this.calculateProfit(totalOver, calcTaxOverParams.energyIncentivesTax.incomeTax);
		const yearlyProfit = this.calculateYearlyProfit(totalProfit, calcTaxOverParams.contractYear) || 0;
		const monthlyProfit = this.calculateMonthlyProfit(yearlyProfit);
		this.updateTaxReductDetails(energyIncentive.taxReductDetails, totalOver, totalProfit, yearlyProfit, monthlyProfit);
		this.updateTaxReduct(energy, yearlyProfit, calcTaxOverParams);
	}

	private computeTotalOver(calcTaxOverParams: iCalcTaxOverParams, energy: string, isOutdated: boolean, taxOver: number, financials: any) {
		if (this.shouldUseOveramortizationForElectricFrance(calcTaxOverParams.countrycode, energy, isOutdated)) {
			return this.calculateTotalOverForFrance(taxOver, calcTaxOverParams);
		}
		return this.calculateTotalOver(taxOver, financials);
	}

	private calculateTotalOver(taxOver: number, financials: { body: number; truck: number }) {
		return this.toFixed((taxOver / 100) * (financials.body + financials.truck));
	}

	private calculateTotalOverForFrance(taxOver: number, calcTaxOverParams: iCalcTaxOverParams) {
		if (calcTaxOverParams.comparisonsSelected.length === 1) {
			return 0;
		}
		const energy = calcTaxOverParams.comparisonsSelected[1].name;
		return this.toFixed((taxOver / 100) * this.calcTaxOverBase(calcTaxOverParams.financials, energy));
	}

	private calculateProfit(totalOver: number, incomeTax: number) {
		return this.toFixed((totalOver * incomeTax) / 100);
	}

	private calculateYearlyProfit(profitTot: number, contractYear: number) {
		return this.toFixed(profitTot / contractYear);
	}

	private calculateMonthlyProfit(profitYear: number) {
		return this.toFixed(profitYear / 12);
	}

	private updateTaxReductDetails(taxReductDetails: any, totOver: number, profitTot: number, profitYear: number, profitMonth: number) {
		taxReductDetails.totOver = totOver;
		taxReductDetails.profitTot = profitTot;
		taxReductDetails.profitYear = profitYear;
		taxReductDetails.profitMonth = profitMonth;
	}

	private updateTaxReduct(energy: string, profitYear: number, params: iCalcTaxOverParams) {
		params.energyIncentivesTax[energy].taxReduct = profitYear;
		this.vehicleCostFormService.incentivesForm.patchValue({ [energy]: { taxReduct: profitYear } });
	}

	private toFixed(value: number): number {
		return +value.toFixed(0) || 0;
	}

	private checkTaxReductOveramortization(taxReductUpdateDate: Date, taxReductLimitDate: Date): boolean {
		return !!taxReductUpdateDate && new Date(taxReductUpdateDate) > taxReductLimitDate;
	}

	private shouldUseOveramortizationForElectricFrance(countrycode: string, energy: string, isOutdated: boolean): boolean {
		return countrycode === GlobalConstant.FR && energy === GlobalConstant.ELECTRIC && isOutdated;
	}
}
